import { useState } from 'react';
import AppWrappedSearchableAutocomplete from '../../AppWrappedSearchableAutocomplete';

/**
 * User Credential input component for Epic/EpicCare stylized to work with Custom App Request Form
 * @param {Object} props properties
 * @param {boolean} props.isDisabled flag for disabled state
 * @returns Custom input
 */
const FunctionalRoleField = ({ isDisabled = false, ...props }) => {
	const FUNCTIONAL_ROLE_API_FAILURE_MESSAGE =
		'Unable to find any functional roles based on your input.  Please try again.';
	const [loadingStatus, setLoadingStatus] = useState(false);
	const [optionsList, setOptionsList] = useState([]);

	/**
	 * Take user input and return a list of search results
	 * @param {string} value user input to search for
	 * @returns {string[]|object[]} list of results based on `value`
	 */
	const onSearchHandler = async value => {
		if (value) {
			let results = [...optionsList];
			if (value.length > 0) {
				results = optionsList.filter(o =>
					o.toLowerCase().includes(value.toLowerCase())
				);
			}
			return results.length > 200 ? results.slice(0, 200) : results;
		} else {
			return [];
		}
	};

	/**
	 * Process search results
	 * @param {string[]|object[]} resultsArr list of search results
	 * @returns {object[]} array of `key`, `value` pairs based on `resultsArr`
	 */
	const onSearchSuccess = resultsArr => {
		return resultsArr
			.map(key => ({
				key: key,
				value: key,
				category: 'Search Results',
			}))
			.concat(
				optionsList
					.filter(opt => !resultsArr.includes(opt))
					.map(key => ({
						key: key,
						value: key,
						category: 'Other Options',
					}))
			);
	};

	/**
	 * Gracefully handle errors during `onSearch` execution of `AppWrappedSearchableAutocomplete`
	 * @param {object} error error caught during `onSearch` workflow
	 * @returns {string[]} default list of results
	 */
	const onSearchFailure = error => {
		props.formik.setStatus({
			error: {
				source: props.name,
				cause: 'Input Failure',
				details: error,
				message: FUNCTIONAL_ROLE_API_FAILURE_MESSAGE,
			},
		});
		return [];
	};

	return (
		<AppWrappedSearchableAutocomplete
			label='Epic Registration/Billing Role'
			onSearchHandler={onSearchHandler}
			onSearchFailure={onSearchFailure}
			onSearchSuccess={onSearchSuccess}
			helperText={
				<>
					This field is disabled with the selection of{' '}
					<em>VIEW ONLY</em> because Epic/EpicCare (Legacy WPA)
					is in the process of being retired.
				</>
			}
			isLoading={loadingStatus}
			isDisabled={isDisabled}
			{...props}
		/>
	);
};

export default FunctionalRoleField;
